import { useState, type FormEvent } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { ArrowLeft, Copy, Settings, Trash2, UserPlus } from "lucide-react";
import type { OrgRole } from "@etus-mindmap/shared/api";
import { useInviteMember, useMembers, useRemoveMember, useSpaces, useUpdateMemberRole } from "../api/hooks";

// Tela (rota) de gestão de membros de uma org/time. /orgs/:id/members e
// /teams/:id/members. Convida por e-mail (gera link), lista e remove.

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

export function OrgMembersRoute() {
  const { orgId } = useParams({ from: "/orgs/$orgId/members" });
  return <MembersPage scope="org" id={Number.parseInt(orgId, 10)} />;
}

export function TeamMembersRoute() {
  const { teamId } = useParams({ from: "/teams/$teamId/members" });
  return <MembersPage scope="team" id={Number.parseInt(teamId, 10)} />;
}

function MembersPage({ scope, id }: { scope: "org" | "team"; id: number }) {
  const { user } = useAuth0();
  const spaces = useSpaces();
  const members = useMembers(scope, id);
  const invite = useInviteMember(scope, id);
  const removeMember = useRemoveMember(scope, id);
  const updateRole = useUpdateMemberRole(scope, id);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("member");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const space =
    scope === "org"
      ? spaces.data?.orgs.find((o) => o.id === id)
      : spaces.data?.teams.find((t) => t.id === id);
  const name = space?.name ?? (scope === "org" ? "Organização" : "Time");
  const canManage = space?.myRole === "owner" || space?.myRole === "admin";

  const onInvite = (e: FormEvent) => {
    e.preventDefault();
    const target = email.trim();
    if (!target.includes("@")) return;
    invite.mutate(
      { email: target, role },
      {
        onSuccess: (res) => {
          setInviteLink(`${window.location.origin}/invite/${res.token}`);
          setEmail("");
        },
      },
    );
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/"
          title="Voltar"
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Membros</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{name}</p>
        </div>
        {scope === "org" && (
          <Link
            to="/orgs/$orgId"
            params={{ orgId: String(id) }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
          >
            <Settings className="h-4 w-4" aria-hidden />
            Configurações
          </Link>
        )}
      </div>

      <div className="max-w-2xl space-y-6">
        {canManage && (
          <form onSubmit={onInvite} className="space-y-2">
            <span className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Convidar por e-mail
            </span>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="pessoa@empresa.com"
                className={inputCls}
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as OrgRole)}
                aria-label="Papel do convidado"
                className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                <option value="member">Membro</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={invite.isPending || !email.includes("@")}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Convidar
              </button>
            </div>
            {inviteLink && (
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-900">
                <code className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-slate-300">{inviteLink}</code>
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-300"
                >
                  <Copy className="h-3 w-3" aria-hidden />
                  {copied ? "Copiado!" : "Copiar"}
                </button>
              </div>
            )}
          </form>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">E-mail</th>
                <th className="px-4 py-2.5 font-medium">Papel</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {members.data?.map((m) => (
                <tr key={m.email} className="bg-white dark:bg-slate-950">
                  <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{m.email}</td>
                  <td className="px-4 py-2.5">
                    {canManage && m.role !== "owner" && m.email !== user?.email ? (
                      <select
                        value={m.role}
                        disabled={updateRole.isPending}
                        onChange={(e) =>
                          updateRole.mutate({ email: m.email, role: e.target.value as OrgRole })
                        }
                        aria-label={`Papel de ${m.email}`}
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      >
                        <option value="member">Membro</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">{m.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {(canManage || m.email === user?.email) && m.role !== "owner" && (
                      <button
                        type="button"
                        onClick={() => removeMember.mutate(m.email)}
                        title={m.email === user?.email ? "Sair" : "Remover"}
                        aria-label="Remover membro"
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
